import type { AudioSettings, Project } from '@cinegrid/core';
import { defaultAudioSettings } from '@cinegrid/core';
import { resolveAssetUrl } from '../media/mediaStore';
import { createAudioFxChain, resolveAudioSettings, type AudioFxHandles } from './audioFx';
import { createSeekController, type SeekController } from './seekController';

type ClipGraph = {
  el: HTMLMediaElement;
  source: MediaElementAudioSourceNode;
  fx: AudioFxHandles;
};

export type MeterLevels = {
  left: number;
  right: number;
  peakLeft: number;
  peakRight: number;
};

/**
 * Preview audio mixer — WebGL videos stay silent; this plays timeline
 * audio through a per-clip FX graph (volume, NR, EQ, compressor, reverb).
 */
export class PreviewAudioEngine {
  private players = new Map<string, ClipGraph>();
  private seekers = new Map<string, SeekController>();
  private loading = new Set<string>();
  private host: HTMLDivElement | null = null;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private splitter: ChannelSplitterNode | null = null;
  private analyserL: AnalyserNode | null = null;
  private analyserR: AnalyserNode | null = null;
  private timeDomainL: Float32Array | null = null;
  private timeDomainR: Float32Array | null = null;
  private peakHoldL = 0;
  private peakHoldR = 0;
  private lastProject: Project | null = null;
  private lastPlayhead = 0;
  private lastPlaying = false;

  private ensureHost() {
    if (this.host) return this.host;
    const el = document.createElement('div');
    el.setAttribute('data-cinegrid-audio', '1');
    el.style.cssText =
      'position:fixed;width:0;height:0;overflow:hidden;pointer-events:none;opacity:0';
    document.body.appendChild(el);
    this.host = el;
    return el;
  }

  private ensureContext() {
    if (this.ctx && this.master) return this.ctx;
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.master.gain.value = 1;

    this.splitter = this.ctx.createChannelSplitter(2);
    this.analyserL = this.ctx.createAnalyser();
    this.analyserR = this.ctx.createAnalyser();
    this.analyserL.fftSize = 2048;
    this.analyserR.fftSize = 2048;
    this.analyserL.smoothingTimeConstant = 0.3;
    this.analyserR.smoothingTimeConstant = 0.3;
    this.timeDomainL = new Float32Array(this.analyserL.fftSize);
    this.timeDomainR = new Float32Array(this.analyserR.fftSize);

    this.master.connect(this.ctx.destination);
    this.master.connect(this.splitter);
    this.splitter.connect(this.analyserL, 0);
    this.splitter.connect(this.analyserR, 1);
    return this.ctx;
  }

  /** Instantaneous + peak-hold levels in 0..1 (linear). */
  getMeterLevels(): MeterLevels {
    if (!this.analyserL || !this.analyserR || !this.timeDomainL || !this.timeDomainR) {
      return { left: 0, right: 0, peakLeft: 0, peakRight: 0 };
    }
    this.analyserL.getFloatTimeDomainData(this.timeDomainL as Float32Array<ArrayBuffer>);
    this.analyserR.getFloatTimeDomainData(this.timeDomainR as Float32Array<ArrayBuffer>);

    const peakOf = (buf: Float32Array) => {
      let p = 0;
      for (let i = 0; i < buf.length; i++) {
        const a = Math.abs(buf[i] ?? 0);
        if (a > p) p = a;
      }
      return Math.min(1, p);
    };

    const left = peakOf(this.timeDomainL);
    const right = peakOf(this.timeDomainR);

    const decay = this.lastPlaying ? 0.96 : 0.85;
    this.peakHoldL = left > this.peakHoldL ? left : this.peakHoldL * decay;
    this.peakHoldR = right > this.peakHoldR ? right : this.peakHoldR * decay;
    if (this.peakHoldL < 0.01) this.peakHoldL = 0;
    if (this.peakHoldR < 0.01) this.peakHoldR = 0;

    return {
      left,
      right,
      peakLeft: this.peakHoldL,
      peakRight: this.peakHoldR,
    };
  }

  destroy() {
    for (const s of this.seekers.values()) s.destroy();
    this.seekers.clear();
    for (const g of this.players.values()) {
      g.el.pause();
      g.el.removeAttribute('src');
      g.el.load();
      g.fx.disconnect();
      try {
        g.source.disconnect();
      } catch {
        /* ignore */
      }
      g.el.remove();
    }
    this.players.clear();
    this.loading.clear();
    void this.ctx?.close();
    this.ctx = null;
    this.master = null;
    this.splitter = null;
    this.analyserL = null;
    this.analyserR = null;
    this.timeDomainL = null;
    this.timeDomainR = null;
    this.host?.remove();
    this.host = null;
  }

  /** Call from play button so playback starts inside a user-gesture. */
  sync(project: Project, playhead: number, isPlaying: boolean) {
    this.lastProject = project;
    this.lastPlayhead = playhead;
    this.lastPlaying = isPlaying;

    const ctx = this.ensureContext();
    if (isPlaying && ctx.state === 'suspended') {
      void ctx.resume().catch(() => undefined);
    }

    const anySolo = project.tracks.some((t) => t.solo);
    const active = new Set<string>();

    const audioAssetIdsAtPlayhead = new Set<string>();
    for (const clip of project.clips) {
      if (clip.kind !== 'audio' || !clip.assetId || clip.muted) continue;
      if (playhead < clip.start || playhead >= clip.start + clip.duration) continue;
      const track = project.tracks.find((t) => t.id === clip.trackId);
      if (!track || track.muted) continue;
      if (anySolo && !track.solo) continue;
      audioAssetIdsAtPlayhead.add(clip.assetId);
    }

    for (const track of project.tracks) {
      if (track.muted) continue;
      if (anySolo && !track.solo) continue;

      const clip = project.clips.find(
        (c) =>
          c.trackId === track.id &&
          playhead >= c.start &&
          playhead < c.start + c.duration &&
          !c.muted &&
          !!c.assetId &&
          (c.kind === 'video' || c.kind === 'audio')
      );
      if (!clip?.assetId) continue;

      if (clip.kind === 'video' && audioAssetIdsAtPlayhead.has(clip.assetId)) {
        continue;
      }

      const asset = project.assets.find((a) => a.id === clip.assetId);
      if (!asset || asset.kind === 'image') continue;

      active.add(clip.id);
      const local = playhead - clip.start;
      const sourceTime = clip.inPoint + local;
      const graph = this.ensurePlayer(clip.id, asset);
      if (!graph) continue;

      const audio = resolveAudioSettings(
        (clip.properties.audio as AudioSettings | undefined) ?? defaultAudioSettings()
      );
      // Also fold transform opacity into gain for audio clips
      const opacity = clip.properties.transform.opacity ?? 1;
      graph.fx.apply({ ...audio, volume: audio.volume * opacity });

      if (!graph.el.src || graph.el.readyState < 1) continue;

      const drift = Math.abs(graph.el.currentTime - sourceTime);
      const seeker = this.seekers.get(clip.id);

      if (isPlaying) {
        if (drift > 0.35 && seeker) seeker.request(sourceTime);
        if (graph.el.paused) {
          void graph.el.play().catch((err) => {
            console.warn('Preview audio play blocked', err);
          });
        }
      } else {
        if (!graph.el.paused) graph.el.pause();
        if (drift > 0.04 && seeker) seeker.request(sourceTime);
      }
    }

    for (const [clipId, g] of this.players) {
      if (!active.has(clipId) && !g.el.paused) g.el.pause();
    }
  }

  private resync() {
    if (this.lastProject) {
      this.sync(this.lastProject, this.lastPlayhead, this.lastPlaying);
    }
  }

  private ensurePlayer(
    clipId: string,
    asset: { id: string; url: string; kind: string; name?: string; mimeType?: string }
  ): ClipGraph | null {
    const existing = this.players.get(clipId);
    if (existing) return existing;

    if (this.loading.has(clipId)) return this.players.get(clipId) ?? null;
    this.loading.add(clipId);

    const ctx = this.ensureContext();
    const master = this.master!;

    const el = document.createElement(asset.kind === 'audio' ? 'audio' : 'video');
    el.preload = 'auto';
    el.muted = false;
    // Route through Web Audio only — keep element audible via graph
    el.volume = 1;
    if (el instanceof HTMLVideoElement) {
      el.playsInline = true;
      el.width = 0;
      el.height = 0;
    }
    this.ensureHost().appendChild(el);

    let source: MediaElementAudioSourceNode;
    try {
      source = ctx.createMediaElementSource(el);
    } catch (err) {
      console.warn('MediaElementSource failed', err);
      this.loading.delete(clipId);
      el.remove();
      return null;
    }

    const fx = createAudioFxChain(ctx);
    source.connect(fx.input);
    fx.output.connect(master);

    const graph: ClipGraph = { el, source, fx };
    this.players.set(clipId, graph);
    this.seekers.set(clipId, createSeekController(el));

    void resolveAssetUrl(asset)
      .then((url) => {
        const g = this.players.get(clipId);
        if (!g) return;
        g.el.src = url;
        g.el.load();
        const onReady = () => {
          g.el.removeEventListener('loadeddata', onReady);
          g.el.removeEventListener('canplay', onReady);
          this.resync();
        };
        g.el.addEventListener('loadeddata', onReady);
        g.el.addEventListener('canplay', onReady);
      })
      .catch((err) => console.warn('Preview audio resolve failed', err))
      .finally(() => this.loading.delete(clipId));

    return graph;
  }
}

let sharedEngine: PreviewAudioEngine | null = null;

export function getPreviewAudioEngine(): PreviewAudioEngine {
  if (!sharedEngine) sharedEngine = new PreviewAudioEngine();
  return sharedEngine;
}
